import { useState, useRef, useCallback, useEffect } from "react";
import { useParams } from "react-router-dom";
import { trpc, toastTrpcError } from "@/lib/trpc";
import { authedFetch } from "@/lib/auth";
import { ingestWeb } from "@/lib/ingest-web";
import { AUTHORITY_LEVELS } from "@/lib/constants";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Globe,
  FileText,
  PenLine,
  Loader2,
  ChevronRight,
  Upload,
  Palette,
  Check,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { LimitReachedBanner } from "@/components/limit-reached-banner";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type TabValue = "web" | "document" | "manual" | "design_system";

// ---------------------------------------------------------------------------
// Crawl depth options (reused from old web-source page)
// ---------------------------------------------------------------------------

const depthOptions = [
  { value: "0", label: "Single page only" },
  { value: "1", label: "1 level deep" },
  { value: "2", label: "2 levels deep" },
  { value: "3", label: "3 levels deep" },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface AddSourceInitialValues {
  url: string;
  name: string;
  authorityLevel?: string;
  language?: string;
  maxDepth?: number;
  maxPages?: number;
}

export function AddSourceDialog({
  open,
  onOpenChange,
  initialValues,
  onSuccess,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialValues?: AddSourceInitialValues;
  onSuccess?: (sourceId: string) => void;
}) {
  const { id: projectId } = useParams<{ id: string }>();
  const utils = trpc.useUtils();

  const [tab, setTab] = useState<TabValue>("web");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // --- Web tab state ---
  const [webUrl, setWebUrl] = useState(initialValues?.url ?? "");
  const [webName, setWebName] = useState(initialValues?.name ?? "");
  const [webNameManual, setWebNameManual] = useState(!!initialValues?.name);
  const [webAuthority, setWebAuthority] = useState(
    initialValues?.authorityLevel ?? "informational",
  );
  const [webLanguage, setWebLanguage] = useState(initialValues?.language ?? "");
  const [webUrlError, setWebUrlError] = useState("");
  const [showCrawlSettings, setShowCrawlSettings] = useState(false);
  const [webDepth, setWebDepth] = useState("0");
  const [webMaxPages, setWebMaxPages] = useState("50");
  const [webInclude, setWebInclude] = useState("");
  const [webExclude, setWebExclude] = useState("");

  // --- Document tab state ---
  const fileRef = useRef<HTMLInputElement>(null);
  const [docFile, setDocFile] = useState<File | null>(null);
  const [docName, setDocName] = useState("");
  const [docAuthority, setDocAuthority] = useState("informational");
  const [docLanguage, setDocLanguage] = useState("");

  // --- Manual tab state ---
  const [manualName, setManualName] = useState("");
  const [manualContent, setManualContent] = useState("");
  const [manualAuthority, setManualAuthority] = useState("informational");
  const [manualLanguage, setManualLanguage] = useState("");

  // --- Design System tab state ---
  const dsFileRef = useRef<HTMLInputElement>(null);
  const [dsFile, setDsFile] = useState<File | null>(null);
  const [dsName, setDsName] = useState("");
  const [dsUrl, setDsUrl] = useState("");
  const [dsUrlError, setDsUrlError] = useState("");
  const [dsAuthority, setDsAuthority] = useState("company_policy");
  const [dsLanguage, setDsLanguage] = useState("");

  // --- Crawl progress state ---
  const [crawlProgress, setCrawlProgress] = useState<{
    stage: string;
    progress: number;
    currentUrl?: string;
    currentTitle?: string;
    totalProcessed?: number;
    chunksCreated?: number;
    linksFound?: number;
  } | null>(null);
  const [crawlDone, setCrawlDone] = useState(false);
  const [crawlError, setCrawlError] = useState<string | null>(null);

  // Sync web tab fields when initialValues change (e.g. opening for a discovery candidate)
  useEffect(() => {
    if (open && initialValues) {
      setTab("web");
      setWebUrl(initialValues.url);
      setWebName(initialValues.name);
      setWebNameManual(!!initialValues.name);
      setWebAuthority(initialValues.authorityLevel ?? "informational");
      setWebLanguage(initialValues.language ?? "");
      setWebUrlError("");
      if (initialValues.maxDepth != null)
        setWebDepth(String(initialValues.maxDepth));
      if (initialValues.maxPages != null)
        setWebMaxPages(String(initialValues.maxPages));
    }
  }, [open, initialValues]);

  // --- Helpers ---

  function resetAll() {
    setWebUrl("");
    setWebName("");
    setWebNameManual(false);
    setWebAuthority("informational");
    setWebLanguage("");
    setWebUrlError("");
    setShowCrawlSettings(false);
    setWebDepth("0");
    setWebMaxPages("50");
    setWebInclude("");
    setWebExclude("");
    setDocFile(null);
    setDocName("");
    setDocAuthority("informational");
    setDocLanguage("");
    setManualName("");
    setManualContent("");
    setManualAuthority("informational");
    setManualLanguage("");
    setDsFile(null);
    setDsName("");
    setDsUrl("");
    setDsUrlError("");
    setDsAuthority("company_policy");
    setDsLanguage("");
    setCrawlProgress(null);
    setCrawlDone(false);
    setCrawlError(null);
    setTab("web");
    if (fileRef.current) fileRef.current.value = "";
    if (dsFileRef.current) dsFileRef.current.value = "";
  }

  function handleClose(open: boolean) {
    if (!open) resetAll();
    onOpenChange(open);
  }

  function validateUrl(value: string): string {
    if (!value.trim()) return "";
    try {
      const parsed = new URL(value);
      if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
        return "URL must use http or https";
      }
      return "";
    } catch {
      return "Enter a valid URL (e.g. https://example.com)";
    }
  }

  const handleUrlBlur = useCallback(() => {
    setWebUrlError(validateUrl(webUrl));
    if (webUrl && !webNameManual) {
      try {
        const parsed = new URL(webUrl);
        const hostname = parsed.hostname.replace(/^www\./, "");
        const suggested = hostname
          .split(".")
          .slice(0, -1)
          .join(" ")
          .replace(/\b\w/g, (c) => c.toUpperCase());
        if (suggested) setWebName(suggested);
      } catch {
        // ignore
      }
    }
  }, [webUrl, webNameManual]);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0] ?? null;
      setDocFile(file);
      if (file && !docName) {
        const nameWithoutExt = file.name.replace(/\.[^.]+$/, "");
        setDocName(nameWithoutExt);
      }
    },
    [docName],
  );

  const handleDsFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0] ?? null;
      setDsFile(file);
      if (file && !dsName) {
        const nameWithoutExt = file.name.replace(/\.[^.]+$/, "");
        setDsName(nameWithoutExt);
      }
    },
    [dsName],
  );

  const handleDsUrlBlur = useCallback(() => {
    if (dsUrl.trim()) setDsUrlError(validateUrl(dsUrl));
    else setDsUrlError("");
  }, [dsUrl]);

  // --- Can submit ---

  const canSubmitWeb =
    webUrl.trim() && !webUrlError && webName.trim() && webAuthority;
  const canSubmitDoc = docFile && docName.trim() && docAuthority;
  const canSubmitManual =
    manualName.trim() && manualContent.trim() && manualAuthority;
  const canSubmitDs =
    dsName.trim() && dsAuthority && (dsFile || (!dsUrlError && dsUrl.trim()));

  const canSubmit =
    !isSubmitting &&
    ((tab === "web" && canSubmitWeb) ||
      (tab === "document" && canSubmitDoc) ||
      (tab === "manual" && canSubmitManual) ||
      (tab === "design_system" && canSubmitDs));

  // --- Submit ---

  async function handleSubmit() {
    if (!canSubmit) return;
    setIsSubmitting(true);

    try {
      if (tab === "web") {
        const error = validateUrl(webUrl);
        if (error) {
          setWebUrlError(error);
          return;
        }

        const includePaths = webInclude
          .split("\n")
          .map((s) => s.trim())
          .filter(Boolean);
        const excludePaths = webExclude
          .split("\n")
          .map((s) => s.trim())
          .filter(Boolean);

        const body = {
          url: webUrl.trim(),
          name: webName.trim(),
          authorityLevel: webAuthority,
          language: webLanguage.trim() || undefined,
          projectId: projectId || undefined,
          crawlConfig: {
            maxDepth: Number(webDepth),
            maxPages: Math.max(1, Math.min(500, Number(webMaxPages) || 50)),
            ...(includePaths.length > 0 && { includePaths }),
            ...(excludePaths.length > 0 && { excludePaths }),
          },
        };

        setCrawlProgress({ stage: "starting", progress: 0 });
        setCrawlDone(false);
        setCrawlError(null);

        const result = await ingestWeb(body, {
          onProgress: (p) => {
            setCrawlProgress(p);
          },
          onDone: () => {
            setCrawlDone(true);
            setCrawlProgress((prev) =>
              prev ? { ...prev, stage: "ready", progress: 100 } : prev,
            );
            toast.success(`Source "${webName.trim()}" added`);
          },
          onError: (msg) => {
            setCrawlError(msg);
          },
        });
        if (result.sourceId) onSuccess?.(result.sourceId);
      } else if (tab === "document") {
        const formData = new FormData();
        formData.append("file", docFile!);
        formData.append(
          "meta",
          JSON.stringify({
            name: docName.trim(),
            authorityLevel: docAuthority,
            language: docLanguage.trim() || undefined,
            projectId: projectId || undefined,
          }),
        );

        const res = await authedFetch("/api/ingest/document", {
          method: "POST",
          body: formData,
        });

        if (!res.ok) {
          const data = await res
            .json()
            .catch(() => ({ error: res.statusText }));
          throw new Error(data.error ?? `Upload failed (${res.status})`);
        }

        toast.success(`Source "${docName.trim()}" added — processing started`);
      } else if (tab === "manual") {
        const body = {
          name: manualName.trim(),
          content: manualContent,
          authorityLevel: manualAuthority,
          language: manualLanguage.trim() || undefined,
          projectId: projectId || undefined,
        };

        const res = await authedFetch("/api/ingest/manual", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });

        if (!res.ok) {
          const data = await res
            .json()
            .catch(() => ({ error: res.statusText }));
          throw new Error(data.error ?? `Request failed (${res.status})`);
        }

        toast.success(`Source "${manualName.trim()}" added`);
      } else if (tab === "design_system") {
        if (dsFile) {
          // Upload file as design system source
          const formData = new FormData();
          formData.append("file", dsFile);
          formData.append(
            "meta",
            JSON.stringify({
              name: dsName.trim(),
              authorityLevel: dsAuthority,
              language: dsLanguage.trim() || undefined,
              projectId: projectId || undefined,
              category: "design_system",
            }),
          );

          const res = await authedFetch("/api/ingest/document", {
            method: "POST",
            body: formData,
          });

          if (!res.ok) {
            const data = await res
              .json()
              .catch(() => ({ error: res.statusText }));
            throw new Error(data.error ?? `Upload failed (${res.status})`);
          }

          toast.success(
            `Design system "${dsName.trim()}" added — processing started`,
          );
        } else if (dsUrl.trim()) {
          // Crawl URL as design system source
          const body = {
            url: dsUrl.trim(),
            name: dsName.trim(),
            authorityLevel: dsAuthority,
            language: dsLanguage.trim() || undefined,
            projectId: projectId || undefined,
            category: "design_system",
          };

          setCrawlProgress({ stage: "starting", progress: 0 });
          setCrawlDone(false);
          setCrawlError(null);

          await ingestWeb(body, {
            onProgress: (p) => {
              setCrawlProgress(p);
            },
            onDone: () => {
              setCrawlDone(true);
              setCrawlProgress((prev) =>
                prev ? { ...prev, stage: "ready", progress: 100 } : prev,
              );
              toast.success(`Design system "${dsName.trim()}" added`);
            },
            onError: (msg) => {
              setCrawlError(msg);
            },
          });
        }
      }

      utils.source.list.invalidate();
      handleClose(false);
    } catch (err) {
      toastTrpcError(err, "Failed to add source");
    } finally {
      setIsSubmitting(false);
    }
  }

  // --- Shared authority + language fields ---

  function AuthoritySelect({
    value,
    onChange,
  }: {
    value: string;
    onChange: (v: string) => void;
  }) {
    return (
      <div className="space-y-2">
        <Label htmlFor="add-source-authority">Authority level</Label>
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger id="add-source-authority" className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {AUTHORITY_LEVELS.map((a) => (
              <SelectItem key={a.value} value={a.value}>
                {a.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  function LanguageInput({
    value,
    onChange,
  }: {
    value: string;
    onChange: (v: string) => void;
  }) {
    return (
      <div className="space-y-2">
        <Label htmlFor="add-source-language">
          Language{" "}
          <span className="text-muted-foreground font-normal">(optional)</span>
        </Label>
        <Input
          id="add-source-language"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="e.g. english, swedish"
        />
      </div>
    );
  }

  // --- Render ---

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add source</DialogTitle>
          <DialogDescription>
            Add a web page, document, or manual text to your knowledge base.
          </DialogDescription>
        </DialogHeader>

        <LimitReachedBanner resource="sources" />

        {crawlProgress && !crawlDone && !crawlError ? (
          /* ── Crawl progress view ── */
          <div className="min-w-0 space-y-4 py-2 overflow-hidden">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold">
                {crawlProgress.stage === "starting"
                  ? "Starting crawl..."
                  : "Crawling"}
              </span>
              <span className="text-xs text-muted-foreground tabular-nums shrink-0 ml-4">
                {crawlProgress.progress}%
              </span>
            </div>

            <div className="h-2 rounded-full bg-muted overflow-hidden">
              <div
                className="h-full rounded-full bg-primary transition-all duration-300"
                style={{ width: `${crawlProgress.progress}%` }}
              />
            </div>

            {crawlProgress.currentTitle && (
              <p className="text-sm truncate">{crawlProgress.currentTitle}</p>
            )}
            {crawlProgress.currentUrl && (
              <p className="text-sm text-muted-foreground truncate">
                {crawlProgress.currentUrl}
              </p>
            )}

            <div className="flex gap-6 text-sm text-muted-foreground">
              {crawlProgress.totalProcessed != null && (
                <div>
                  <span className="font-semibold text-foreground">
                    {crawlProgress.totalProcessed}
                  </span>{" "}
                  pages
                </div>
              )}
              {crawlProgress.chunksCreated != null && (
                <div>
                  <span className="font-semibold text-foreground">
                    {crawlProgress.chunksCreated}
                  </span>{" "}
                  chunks
                </div>
              )}
              {crawlProgress.linksFound != null && (
                <div>
                  <span className="font-semibold text-foreground">
                    {crawlProgress.linksFound}
                  </span>{" "}
                  links
                </div>
              )}
            </div>
          </div>
        ) : crawlError ? (
          /* ── Error view ── */
          <div className="space-y-3 py-2">
            <p className="text-sm text-destructive font-medium">{crawlError}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setCrawlError(null);
                setCrawlProgress(null);
                setIsSubmitting(false);
              }}
            >
              Try again
            </Button>
          </div>
        ) : crawlDone ? (
          /* ── Done view ── */
          <div className="space-y-3 py-2 text-center">
            <div className="mx-auto flex size-10 items-center justify-center rounded-full bg-mark-helps/8">
              <Check className="size-5 text-finding-helps" />
            </div>
            <p className="text-sm font-semibold">Crawl complete</p>
            {crawlProgress && (
              <div className="flex justify-center gap-6 text-sm text-muted-foreground">
                {crawlProgress.totalProcessed != null && (
                  <span>{crawlProgress.totalProcessed} pages</span>
                )}
                {crawlProgress.chunksCreated != null && (
                  <span>{crawlProgress.chunksCreated} chunks</span>
                )}
              </div>
            )}
          </div>
        ) : (
          <Tabs value={tab} onValueChange={(v) => setTab(v as TabValue)}>
            <TabsList className="w-full h-auto p-1 grid grid-cols-2 sm:grid-cols-4 gap-1">
              <TabsTrigger value="web" className="text-xs sm:text-sm py-1.5">
                <Globe className="size-3.5" />
                Web
              </TabsTrigger>
              <TabsTrigger
                value="document"
                className="text-xs sm:text-sm py-1.5"
              >
                <FileText className="size-3.5" />
                Document
              </TabsTrigger>
              <TabsTrigger value="manual" className="text-xs sm:text-sm py-1.5">
                <PenLine className="size-3.5" />
                Manual
              </TabsTrigger>
              <TabsTrigger
                value="design_system"
                className="text-xs sm:text-sm py-1.5"
              >
                <Palette className="size-3.5" />
                Design system
              </TabsTrigger>
            </TabsList>

            {/* ── Web tab ── */}
            <TabsContent value="web" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="add-source-web-url">URL</Label>
                <Input
                  id="add-source-web-url"
                  type="url"
                  value={webUrl}
                  onChange={(e) => {
                    setWebUrl(e.target.value);
                    if (webUrlError)
                      setWebUrlError(validateUrl(e.target.value));
                  }}
                  onBlur={handleUrlBlur}
                  placeholder="https://example.com/docs/page"
                  aria-invalid={!!webUrlError}
                />
                {webUrlError && (
                  <p className="text-sm text-destructive">{webUrlError}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="add-source-web-name">Name</Label>
                <Input
                  id="add-source-web-name"
                  value={webName}
                  onChange={(e) => {
                    setWebName(e.target.value);
                    setWebNameManual(e.target.value.trim().length > 0);
                  }}
                  placeholder="Auto-filled from URL"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <AuthoritySelect
                  value={webAuthority}
                  onChange={setWebAuthority}
                />
                <LanguageInput value={webLanguage} onChange={setWebLanguage} />
              </div>

              <Collapsible
                open={showCrawlSettings}
                onOpenChange={setShowCrawlSettings}
              >
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                    aria-label="Toggle Crawl settings"
                  >
                    <ChevronRight
                      className={cn(
                        "size-3.5 transition-transform",
                        showCrawlSettings && "rotate-90",
                      )}
                      aria-hidden="true"
                    />
                    Crawl settings
                    {!showCrawlSettings && (
                      <span className="text-xs">
                        (
                        {depthOptions.find((o) => o.value === webDepth)
                          ?.label ?? "Single page"}
                        )
                      </span>
                    )}
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-4 mt-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="add-source-crawl-depth">
                        Crawl depth
                      </Label>
                      <Select value={webDepth} onValueChange={setWebDepth}>
                        <SelectTrigger
                          id="add-source-crawl-depth"
                          className="w-full"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {depthOptions.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="add-source-max-pages">Max pages</Label>
                      <Input
                        id="add-source-max-pages"
                        type="number"
                        min={1}
                        max={500}
                        value={webMaxPages}
                        onChange={(e) => setWebMaxPages(e.target.value)}
                      />
                    </div>
                  </div>

                  {Number(webDepth) > 0 && (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="add-source-include-paths">
                          Include Paths{" "}
                          <span className="text-muted-foreground font-normal">
                            (optional)
                          </span>
                        </Label>
                        <Textarea
                          id="add-source-include-paths"
                          value={webInclude}
                          onChange={(e) => setWebInclude(e.target.value)}
                          placeholder={"/docs/*\n/api/*"}
                          rows={2}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="add-source-exclude-paths">
                          Exclude Paths{" "}
                          <span className="text-muted-foreground font-normal">
                            (optional)
                          </span>
                        </Label>
                        <Textarea
                          id="add-source-exclude-paths"
                          value={webExclude}
                          onChange={(e) => setWebExclude(e.target.value)}
                          placeholder={"/blog/*\n/login"}
                          rows={2}
                        />
                      </div>
                    </div>
                  )}
                </CollapsibleContent>
              </Collapsible>
            </TabsContent>

            {/* ── Document tab ── */}
            <TabsContent value="document" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>File</Label>
                <div
                  className={cn(
                    "flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-6 transition-colors cursor-pointer hover:border-primary/50 hover:bg-muted/50",
                    docFile && "border-primary/30 bg-primary/5",
                  )}
                  onClick={() => fileRef.current?.click()}
                >
                  <Upload className="size-6 text-muted-foreground" />
                  {docFile ? (
                    <p className="text-sm font-medium">{docFile.name}</p>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      Click to select a PDF or DOCX file
                    </p>
                  )}
                  <input
                    ref={fileRef}
                    type="file"
                    className="hidden"
                    accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    onChange={handleFileChange}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="add-source-doc-name">Name</Label>
                <Input
                  id="add-source-doc-name"
                  value={docName}
                  onChange={(e) => setDocName(e.target.value)}
                  placeholder="Pre-filled from filename"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <AuthoritySelect
                  value={docAuthority}
                  onChange={setDocAuthority}
                />
                <LanguageInput value={docLanguage} onChange={setDocLanguage} />
              </div>
            </TabsContent>

            {/* ── Manual tab ── */}
            <TabsContent value="manual" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="add-source-manual-name">Name</Label>
                <Input
                  id="add-source-manual-name"
                  value={manualName}
                  onChange={(e) => setManualName(e.target.value)}
                  placeholder="e.g. Internal Safety Guidelines"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="add-source-manual-content">
                  Content{" "}
                  <span className="text-muted-foreground font-normal">
                    (markdown, max 50k chars)
                  </span>
                </Label>
                <Textarea
                  id="add-source-manual-content"
                  value={manualContent}
                  onChange={(e) => setManualContent(e.target.value)}
                  placeholder="Paste or type your content here..."
                  rows={8}
                  maxLength={50_000}
                />
                {manualContent.length > 0 && (
                  <p className="text-xs text-muted-foreground text-right tabular-nums">
                    {manualContent.length.toLocaleString()} / 50,000
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <AuthoritySelect
                  value={manualAuthority}
                  onChange={setManualAuthority}
                />
                <LanguageInput
                  value={manualLanguage}
                  onChange={setManualLanguage}
                />
              </div>
            </TabsContent>

            {/* ── Design System tab ── */}
            <TabsContent value="design_system" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>
                  File{" "}
                  <span className="text-muted-foreground font-normal">
                    (PDF, DOCX)
                  </span>
                </Label>
                <div
                  className={cn(
                    "flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-6 transition-colors cursor-pointer hover:border-primary/50 hover:bg-muted/50",
                    dsFile && "border-primary/30 bg-primary/5",
                  )}
                  onClick={() => dsFileRef.current?.click()}
                >
                  <Upload className="size-6 text-muted-foreground" />
                  {dsFile ? (
                    <p className="text-sm font-medium">{dsFile.name}</p>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      Upload your design system documentation
                    </p>
                  )}
                  <input
                    ref={dsFileRef}
                    type="file"
                    className="hidden"
                    accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    onChange={handleDsFileChange}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="add-source-ds-url">
                  URL{" "}
                  <span className="text-muted-foreground font-normal">
                    (alternative to file upload)
                  </span>
                </Label>
                <Input
                  id="add-source-ds-url"
                  type="url"
                  value={dsUrl}
                  onChange={(e) => {
                    setDsUrl(e.target.value);
                    if (dsUrlError) setDsUrlError(validateUrl(e.target.value));
                  }}
                  onBlur={handleDsUrlBlur}
                  placeholder="https://design.example.com/tokens"
                  disabled={!!dsFile}
                  aria-invalid={!!dsUrlError}
                />
                {dsUrlError && (
                  <p className="text-sm text-destructive">{dsUrlError}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="add-source-ds-name">Name</Label>
                <Input
                  id="add-source-ds-name"
                  value={dsName}
                  onChange={(e) => setDsName(e.target.value)}
                  placeholder="e.g. Acme Design System v2"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <AuthoritySelect
                  value={dsAuthority}
                  onChange={setDsAuthority}
                />
                <LanguageInput value={dsLanguage} onChange={setDsLanguage} />
              </div>
            </TabsContent>
          </Tabs>
        )}

        <DialogFooter>
          {crawlDone ? (
            <Button onClick={() => handleClose(false)}>Done</Button>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => handleClose(false)}
                disabled={isSubmitting && !crawlError}
              >
                Cancel
              </Button>
              {!crawlProgress && !crawlError && (
                <Button onClick={handleSubmit} disabled={!canSubmit}>
                  {isSubmitting && (
                    <Loader2 className="size-3.5 animate-spin" />
                  )}
                  {tab === "web"
                    ? "Start Crawl"
                    : tab === "document"
                      ? "Upload"
                      : tab === "design_system"
                        ? dsFile
                          ? "Upload"
                          : "Start Crawl"
                        : "Add Source"}
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
